import { useMemo, useRef, useState } from "react";
import { Card, Col, Container, Row, Table, Badge, Button as RBButton, Modal } from "react-bootstrap";
import { ColDef, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { useLoaderData, useNavigate } from "react-router-dom";
import { PlanDetails, PlanPriceDetails, SubscriberInfo } from "./Data/ApiTypes";
import { cents, showMoney, showMoneyShort, toCents } from "./Data/Money";
import { HeaderText } from "./Controls/HeaderText";
import { subtract } from "dinero.js";
import { Check, CheckCircleFill, Clipboard, Copy, InfoCircle, Pencil, QrCode } from "react-bootstrap-icons";
import { colText, colNumber, DataGrid } from "./Controls/DataGrid";
import { useParamIds } from "./Data/Common";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { Button } from "./Controls/Button";
import { Api } from "./Data/Api";
import { PlanPriceAddModal } from "./FacilityPlanPriceAddModal";
import { PlanPriceAddReq } from "./Data/ApiTypes";
import { createInstant } from "./Data/ApiConverters";
import "./FacilityPlanDetailsPage.scss";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { canPerformAction, extractClaims } from "./helpers/AuthorizationManager";

const COPY_ICON_RESET_MS = 3000;

function CouponCodeCopyModal({
    show,
    code,
    priceDisplay,
    onHide,
    onExited,
}: {
    show: boolean;
    code: string;
    priceDisplay: string;
    onHide: () => void;
    onExited: () => void;
}) {
    return (
        <Modal
            show={show}
            onHide={onHide}
            onExited={onExited}
            centered
            backdrop={true}
            keyboard={true}
            aria-modal="true"
            role="dialog"
            className="coupon-code-copy-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0" />
            <Modal.Body className="text-center px-4 pt-0 pb-4">
                <div className="ccm-check-ring mx-auto mb-3">
                    <CheckCircleFill size={40} className="copy-icon-accent" />
                </div>

                <h5 className="fw-bold mb-1">{priceDisplay} Code Copied!</h5>
                <p className="text-muted small mb-3">Ready to share with a parker</p>

                <div className="ccm-code-card mx-auto mb-3 p-3 rounded-3 text-start">
                    <span className="ccm-code-label d-block text-uppercase small mb-1">Price Code</span>
                    <div className="d-flex align-items-center justify-content-between">
                        <span className="ccm-code-value font-monospace fw-bold">{code}</span>
                        <span className="ccm-clipboard-badge d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 small">
                            <Clipboard size={12} /> On clipboard
                        </span>
                    </div>
                </div>

                <div className="ccm-info-box d-flex align-items-start gap-2 text-start rounded-3 p-3 mx-auto mb-3">
                    <InfoCircle className="copy-icon-accent flex-shrink-0 mt-1" />
                    <span className="small">
                        Share this code with a parker. When entered at checkout, they'll receive this rate for their <strong>first month and all subsequent renewals</strong>.
                    </span>
                </div>

                <Button className="w-100" onClick={onHide}>Done</Button>
            </Modal.Body>
        </Modal>
    );
}

export function FacilityPlanDetailsPage() {
    const nav = useNavigate();
    const { facilityId, planId } = useParamIds();
    const [auth] = useAtom(authAtom);
    const canEditPlan = canPerformAction(extractClaims(auth), 'edit-plan');
    const planData = useLoaderData() as PlanDetails;
    const [plan, setPlan] = useState<PlanDetails>(planData);
    const [showAddPrice, setShowAddPrice] = useState(false);
    const [selPrice, setSelPrice] = useState<PlanPriceDetails>( plan.prices[0] );
    // Price code copy: show flag drives visibility; data persists through fade-out
    const [showCouponModal, setShowCouponModal] = useState(false);
    const [couponModalData, setCouponModalData] = useState<{ code: string; priceDisplay: string }>({ code: "", priceDisplay: "" });
    const [copiedRowId, setCopiedRowId] = useState<number | null>(null);
    const copyTriggerRef = useRef<HTMLButtonElement | null>(null);
    const resetIconTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    function openCopyModal(code: string, priceDisplay: string, planPriceId: number, trigger: HTMLButtonElement) {
        if (resetIconTimerRef.current) {
            clearTimeout(resetIconTimerRef.current);
            resetIconTimerRef.current = null;
        }
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(code).catch(() => {});
        }
        copyTriggerRef.current = trigger;
        setCouponModalData({ code, priceDisplay });
        setShowCouponModal(true);
        setCopiedRowId(planPriceId);
        resetIconTimerRef.current = setTimeout(() => {
            setCopiedRowId(null);
            resetIconTimerRef.current = null;
        }, COPY_ICON_RESET_MS);
    }

    function closeCopyModal() {
        if (resetIconTimerRef.current) {
            clearTimeout(resetIconTimerRef.current);
            resetIconTimerRef.current = null;
        }
        setShowCouponModal(false);
        setCopiedRowId(null);
    }

    function onCouponModalExited() {
        setCouponModalData({ code: "", priceDisplay: "" });
        copyTriggerRef.current?.focus();
    }

    function totalSubscribersImpl( subInfo: SubscriberInfo[] ) {
        return subInfo.map( x => x.quantity ).reduce( ( a, b ) => a + b, 0 );
    }

    function totalMonthlyImpl( p: PlanPriceDetails, numSpaces: number ): any {
        return cents( toCents( p.price ) * numSpaces );
    }

    const columns = useMemo<ColDef<SubscriberInfo>[]>( () => [
        colText(   'name'         ),
        colText(   'emailAddress' ),
        colText(   'phoneNumber'  ),
        {
            headerName: "Quantity",
            valueFormatter: (v: ValueFormatterParams<SubscriberInfo>) =>
            v.data?.quantity != null ? v.data.quantity.toString() : "0", 
        },
        { headerName: "Monthly MRR",
            valueFormatter: (v: ValueFormatterParams<SubscriberInfo>) => showMoney( totalMonthlyImpl( selPrice, v.data?.quantity! ) ),
        }
    ], [selPrice] );

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
    };

    const sortedPrices = plan.prices.toSorted( ( a, b ) => toCents( subtract( a.price, b.price ) ) );

    function handleAddPrice(req: PlanPriceAddReq) {
        Api.facilityPlanPriceAdd(facilityId, planId, req).then(
            resp => {
                if (resp.isOk) {
                    // Create a new PlanPriceDetails object from the response
                    const newPrice: PlanPriceDetails = {
                        planPriceId: resp.value.planPriceId,
                        planId: planId,
                        name: resp.value.name,
                        price: cents(resp.value.price),
                        createdByStaffId: 0, // This will be set by the server
                        createdWhen: createInstant(new Date().toISOString()),
                        subscribers: [],
                        couponCode: resp.value.couponCode
                    };

                    // Update the plan state with the new price
                    setPlan(prevPlan => ({
                        ...prevPlan,
                        prices: [...prevPlan.prices, newPrice]
                    }));
                    // Set the new price as selected
                    setSelPrice(newPrice);
                }
            }
        );
    }

    return (
        <Container fluid className="pt-2 h-100" >
            <PlanPriceAddModal
                show={showAddPrice}
                setShow={setShowAddPrice}
                onSave={handleAddPrice}
                prices={plan.prices}
            />
            <CouponCodeCopyModal
                show={showCouponModal}
                code={couponModalData.code}
                priceDisplay={couponModalData.priceDisplay}
                onHide={closeCopyModal}
                onExited={onCouponModalExited}
            />
           
            <Row className="h-100">
                <Col className="h-100" style={{ display: "grid", gridTemplateRows: "auto 1fr auto", gridAutoColumns: "1fr" }}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <RBButton variant="link" style={{marginRight:"-10px"}} onClick={() => nav(-1)}>← Back</RBButton>
                        <PageBar title={plan.name}>
                            {plan.isPrivate && (
                                <Badge className="fs-6">Private Plan</Badge>
                            )}
                        </PageBar>
                        <div className="d-none d-md-flex gap-2">
                            {canEditPlan && (
                            <ToolButton onClick={ () => nav( `/facility/${facilityId}/plan/${planId}/edit` ) }>
                                <Pencil color="maroon" /> Edit Plan
                            </ToolButton>
                            )}
                            {!plan.isPrivate && (
                            <ToolButton onClick={ () => nav( `/facility/${facilityId}/plan/${planId}/sign` ) }>
                                <QrCode color="maroon" /> View QR Code
                            </ToolButton>
                             )}
                        </div>
                        <div className="d-flex d-md-none flex-wrap gap-2 w-100 ms-2 mb-2">
                            {canEditPlan && (
                            <ToolButton onClick={ () => nav( `/facility/${facilityId}/plan/${planId}/edit` ) }>
                                <Pencil color="maroon" /> Edit Plan
                            </ToolButton>
                            )}
                            {!plan.isPrivate && (
                            <ToolButton onClick={ () => nav( `/facility/${facilityId}/plan/${planId}/sign` ) }>
                                <QrCode color="maroon" /> View QR Code
                            </ToolButton>
                             )}
                        </div>
                    </div>
                    <Container fluid className="g-0 py-0 my-0 h-100">
                        <Row className="h-100">
                            <Col xs="12" md="4">
                                <Card className="p-0 m-0 gap-0">
                                    <Card.Header>
                                        <HeaderText className="text-center">
                                            Prices
                                        </HeaderText>
                                    </Card.Header>
                                    <Card.Body className="p-0 m-0 gap-0">
                                    <Table variant="hover">
                                    <thead>
                                        <tr>
                                        <th className="text-center">Price</th>
                                        <th className="text-center">Parkers</th>
                                        <th className="text-center">MRR</th>
                                        <th className="text-center">Price Code</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedPrices.map((p) => {
                                        const sel = p === selPrice ? "bg-primary text-white" : "";
                                        const monthly = totalMonthlyImpl(p, totalSubscribersImpl(p.subscribers));

                                        return (
                                            <tr
                                            key={p.planPriceId}
                                            className={`text-center ${sel}`}
                                            aria-selected={p === selPrice}
                                            onClick={() => setSelPrice(p)}
                                            style={{ cursor: "pointer" }}
                                            >
                                            <td className={`${sel} text-end pe-3`}>{showMoney(p.price)}</td>
                                            <td className={`${sel} text-center`}>
                                                {totalSubscribersImpl(p.subscribers)}
                                            </td>
                                            <td className={`${sel} text-end pe-3`}>
                                                {showMoneyShort(monthly)}
                                            </td>
                                            <td
                                                className={`${sel} text-center`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <span className="d-inline-flex align-items-center gap-1 justify-content-center">
                                                    {p.couponCode}
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 border-0 align-middle"
                                                        style={{ cursor: "pointer", lineHeight: 1 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openCopyModal(p.couponCode, showMoney(p.price), p.planPriceId, e.currentTarget);
                                                        }}
                                                        title="Copy price code"
                                                        aria-label="Copy price code"
                                                    >
                                                        {p.planPriceId === copiedRowId ? (
                                                            <Check className={`small ${sel ? "text-white" : "copy-icon-accent"}`} />
                                                        ) : (
                                                            <Copy className={`small ${sel ? "text-white" : ""}`} />
                                                        )}
                                                    </button>
                                                </span>
                                            </td>
                                            </tr>
                                        );
                                        })}
                                    </tbody>
                                    </Table>
                                    </Card.Body>
                                    {canEditPlan && (
                                    <Card.Footer className="p-2">
                                        <Button className="w-100" onClick={() => setShowAddPrice(true)}>
                                            Add Price
                                        </Button>
                                    </Card.Footer>
                                    )}
                                </Card>
                            </Col>
                            <Col xs="8" className="h-100 d-none d-md-block">
                                <div style={{ height: "100%", width: '100%' }}>
                                    <DataGrid
                                        rowData={selPrice.subscribers}
                                        columnDefs={columns}
                                        onGridReady={onGridReady}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </Col>
            </Row>
        </Container>
    );
}

